// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  ArrowForwardOutlined,
  CheckOutlined,
} from '@/constants/hardfin-standard-icons';
import { pluralize } from '@/utilities/pluralize';
import { ReactNode } from 'react';

interface SerialStatus {
  text: string;
  icon: ReactNode | null;
  color: string;
}

/**
 * Formats a serial count with proper pluralization.
 *
 * @param count - The number of serials
 * @param includeCount - Whether to include the count in the output (default: true)
 * @returns Formatted string like "1 serial" or "5 serials" or just "serial"/"serials"
 */
function formatSerialCountText(count: number, includeCount = true): string {
  return pluralize(count, {
    singular: 'serial',
    plural: 'serials',
    includeCount,
  });
}

/**
 * Formats the invalid serial message.
 *
 * @param invalidCount - Number of invalid serials
 * @param totalCount - Total number of serials entered
 * @returns Formatted message like "1 out of 5 serials is invalid"
 */
function formatInvalidSerialMessage(
  invalidCount: number,
  totalCount: number
): string {
  const serialText = formatSerialCountText(totalCount, false);
  const verb = invalidCount === 1 ? 'is' : 'are';
  return `${invalidCount} out of ${totalCount} ${serialText} ${verb} invalid`;
}

/**
 * Determines the appropriate color for serial status based on error and submission state.
 *
 * @param baseColor - The base color to use when no error conditions apply
 * @param hasError - Whether there is a form validation error
 * @param hasAttemptedSubmit - Whether the user has attempted to submit the form
 * @returns Material-UI color string
 */
function getSerialStatusColor(
  baseColor: string,
  hasError: boolean,
  hasAttemptedSubmit: boolean
): string {
  if (hasError && hasAttemptedSubmit) {
    return 'error.main';
  }
  if (hasAttemptedSubmit && baseColor === 'warning') {
    return 'warning.dark';
  }
  return baseColor;
}

/**
 * Determines the serial status display information based on current state.
 *
 * @param serialCount - Number of serials entered
 * @param expectedQuantity - Expected quantity to ship
 * @param invalidSerialCount - Number of invalid serials
 * @param hasFormError - Whether there is a form validation error
 * @param hasAttemptedSubmit - Whether the user has attempted to submit
 * @returns Object containing text, icon, and color for display
 */
export function getSerialStatus(
  serialCount: number,
  expectedQuantity: number,
  invalidSerialCount: number,
  hasFormError: boolean,
  hasAttemptedSubmit: boolean
): SerialStatus {
  // Invalid serials present
  if (invalidSerialCount > 0) {
    return {
      text: formatInvalidSerialMessage(invalidSerialCount, serialCount),
      icon: null,
      color: 'error.main',
    };
  }

  // Complete (0 serials, 0 expected)
  if (serialCount === 0 && expectedQuantity === 0) {
    return {
      text: 'Complete',
      icon: <CheckOutlined fontSize="small" />,
      color: 'success.main',
    };
  }

  // No serials entered (0 serials, but some expected)
  if (serialCount === 0) {
    return {
      text: 'No serials entered',
      icon: <ArrowForwardOutlined fontSize="small" />,
      color: getSerialStatusColor(
        'text.secondary',
        hasFormError,
        hasAttemptedSubmit
      ),
    };
  }

  // Exact match (serials === expected)
  if (serialCount === expectedQuantity) {
    return {
      text: `${formatSerialCountText(serialCount)} entered`,
      icon: <CheckOutlined fontSize="small" />,
      color: 'success.main',
    };
  }

  // Too many serials (serials > expected)
  if (serialCount > expectedQuantity) {
    const extra = serialCount - expectedQuantity;
    return {
      text: `${formatSerialCountText(serialCount)} entered (${extra} extra)`,
      icon: null,
      color: 'error.main',
    };
  }

  // Too few serials (serials < expected)
  const needed = expectedQuantity - serialCount;
  return {
    text: `${formatSerialCountText(serialCount)} entered (${needed} needed)`,
    icon: null,
    color: getSerialStatusColor('warning', hasFormError, hasAttemptedSubmit),
  };
}
